import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocalizedContent } from "@/hooks/use-localized-content";

const CONSENT_KEY = "rgpd_consent";

export const hasRGPDConsent = (): boolean => {
  return localStorage.getItem(CONSENT_KEY) === "accepted";
};

const RGPDConsent = () => {
  const [visible, setVisible] = useState(false);
  const { c } = useLocalizedContent();

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, "accepted");
    setVisible(false);
    // Dispatch storage event for same-tab listeners
    window.dispatchEvent(new Event("storage"));
  };

  const handleRefuse = () => {
    localStorage.setItem(CONSENT_KEY, "refused");
    setVisible(false);
    window.dispatchEvent(new Event("storage"));
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 60 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed inset-x-0 bottom-0 z-[9999] p-3 sm:p-4 md:p-6"
        >
          <div className="max-w-lg mx-auto rounded-2xl bg-card border border-border shadow-2xl p-4 sm:p-5 md:p-6">
            <div className="flex items-start gap-3 mb-3 sm:mb-4">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Shield size={18} className="text-primary sm:hidden" />
                <Shield size={20} className="text-primary hidden sm:block" />
              </div>
              <div className="min-w-0">
                <p className="font-display text-sm sm:text-base font-bold text-foreground mb-1">
                  {c("rgpd_title", "_none_", "Proteção de Dados (RGPD)")}
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {c("rgpd_message", "_none_", "Este site recolhe dados pessoais (nome, email, telefone) para gestão de agendamentos, conforme o RGPD (UE 2016/679) e a Lei n.º 58/2019.")}
                </p>
              </div>
            </div>

            <div className="flex flex-row items-center gap-2 sm:gap-3">
              <Button
                onClick={handleAccept}
                size="sm"
                className="rounded-full flex-1 text-xs sm:text-sm"
              >
                {c("rgpd_accept", "_none_", "Aceitar")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefuse}
                className="rounded-full flex-1 text-xs sm:text-sm"
              >
                {c("rgpd_refuse", "_none_", "Recusar")}
              </Button>
              <a
                href="/politica-privacidade"
                className="inline-flex items-center justify-center gap-1 text-[10px] sm:text-xs text-primary hover:underline shrink-0"
              >
                <ExternalLink size={10} className="sm:hidden" />
                <ExternalLink size={12} className="hidden sm:block" />
                {c("rgpd_more", "_none_", "Saber mais")}
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RGPDConsent;
